import React, { useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import {
    listEmailTemplates,
    getEmailRecipients,
    previewEmails,
    sendEmails,
} from "../../services/ledgerService";

const SEGMENTS = [
    { value: "", label: "All segments" },
    { value: "active", label: "Active" },
    { value: "latent", label: "Latent" },
    { value: "tire_kicker", label: "Tire-kickers" },
];

// parsePasted turns a free-text blob of addresses (comma / whitespace / newline
// separated) into recipient rows with blank personalization. Templates render
// safely with empty fields (e.g. "there" for first name).
function parsePasted(text) {
    return text
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes("@"))
        .map((email) => ({ email, firstName: "", lastName: "", gamesHosted: 0, lastGameDate: "", hostType: "" }));
}

// mergeRecipients dedupes by email, preferring the DB-segmented row (which
// carries personalization) over a pasted bare address.
function mergeRecipients(fromSegment, pasted) {
    const byEmail = new Map();
    for (const r of pasted) byEmail.set(r.email.toLowerCase(), r);
    for (const r of fromSegment) byEmail.set(r.email.toLowerCase(), r);
    return [...byEmail.values()];
}

const EmailCampaign = () => {
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [segment, setSegment] = useState("");
    const [segmentRecipients, setSegmentRecipients] = useState([]);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [pasted, setPasted] = useState("");
    const [previews, setPreviews] = useState(null);
    const [selectedPreview, setSelectedPreview] = useState(0);
    const [previewing, setPreviewing] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        listEmailTemplates()
            .then((t) => {
                setTemplates(t);
                if (t.length) setTemplateId(t[0].id);
            })
            .catch((e) => setError(e.message));
    }, []);

    // Re-load segmented recipients whenever the segment changes.
    useEffect(() => {
        setLoadingRecipients(true);
        getEmailRecipients(segment)
            .then(setSegmentRecipients)
            .catch((e) => setError(e.message))
            .finally(() => setLoadingRecipients(false));
    }, [segment]);

    const recipients = useMemo(
        () => mergeRecipients(segmentRecipients, parsePasted(pasted)),
        [segmentRecipients, pasted]
    );

    const selectedTemplate = templates.find((t) => t.id === templateId);

    const handlePreview = async () => {
        setError(null);
        setPreviews(null);
        if (!templateId || recipients.length === 0) return;
        setPreviewing(true);
        try {
            const result = await previewEmails({ templateId, recipients });
            setPreviews(result);
            setSelectedPreview(0);
        } catch (e) {
            setError(e.message);
        } finally {
            setPreviewing(false);
        }
    };

    const handleSend = async () => {
        setError(null);
        setSending(true);
        try {
            const result = await sendEmails({ templateId, recipients });
            setSendResult(result);
            setConfirming(false);
        } catch (e) {
            setError(e.message);
            setConfirming(false);
        } finally {
            setSending(false);
        }
    };

    const preview = previews && previews[selectedPreview];

    return (
        <Container className="py-4 admin-page">
            <h1 className="mb-4">Email campaign</h1>

            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
            {sendResult && (
                <Alert variant="success" onClose={() => setSendResult(null)} dismissible>
                    Campaign <code>{sendResult.campaignId}</code> queued — {sendResult.enqueued} email
                    {sendResult.enqueued === 1 ? "" : "s"} enqueued. Delivery happens via the worker (~1/min).
                </Alert>
            )}

            <Row className="g-4">
                <Col lg={5}>
                    <Card className="mb-4">
                        <Card.Body>
                            <Card.Title className="h6 text-uppercase text-muted">1 · Template</Card.Title>
                            <Form.Select
                                value={templateId}
                                onChange={(e) => setTemplateId(e.target.value)}
                                aria-label="Template"
                            >
                                {templates.map((t) => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </Form.Select>
                            {selectedTemplate && (
                                <Form.Text muted>{selectedTemplate.description}</Form.Text>
                            )}
                        </Card.Body>
                    </Card>

                    <Card className="mb-4">
                        <Card.Body>
                            <Card.Title className="h6 text-uppercase text-muted">2 · Recipients</Card.Title>
                            <Form.Label className="small mb-1">Segment</Form.Label>
                            <Form.Select
                                value={segment}
                                onChange={(e) => setSegment(e.target.value)}
                                aria-label="Segment"
                                className="mb-3"
                            >
                                {SEGMENTS.map((s) => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </Form.Select>

                            <Form.Label className="small mb-1">Paste extra addresses</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={pasted}
                                onChange={(e) => setPasted(e.target.value)}
                                placeholder="alice@example.com, bob@example.com"
                            />
                            <Form.Text muted>Pasted addresses get blank personalization.</Form.Text>

                            <div className="mt-3">
                                <Badge bg="secondary">{recipients.length} recipient{recipients.length === 1 ? "" : "s"}</Badge>
                                {loadingRecipients && <Spinner animation="border" size="sm" className="ms-2" />}
                            </div>
                        </Card.Body>
                    </Card>

                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-primary"
                            onClick={handlePreview}
                            disabled={!templateId || recipients.length === 0 || previewing}
                        >
                            {previewing ? <Spinner animation="border" size="sm" /> : "Preview"}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => setConfirming(true)}
                            disabled={!templateId || recipients.length === 0}
                        >
                            Send…
                        </Button>
                    </div>

                    {recipients.length > 0 && (
                        <Table striped hover responsive size="sm" className="mt-4">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Name</th>
                                    <th>Segment</th>
                                    <th className="text-end">Games</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recipients.map((r) => (
                                    <tr key={r.email}>
                                        <td>{r.email}</td>
                                        <td>{[r.firstName, r.lastName].filter(Boolean).join(" ") || <em>—</em>}</td>
                                        <td>{r.hostType ? <Badge bg="info">{r.hostType}</Badge> : <em>pasted</em>}</td>
                                        <td className="text-end">{r.gamesHosted || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Col>

                <Col lg={7}>
                    <Card className="h-100">
                        <Card.Body>
                            <Card.Title className="h6 text-uppercase text-muted d-flex justify-content-between align-items-center">
                                <span>Preview</span>
                                {previews && previews.length > 1 && (
                                    <Form.Select
                                        size="sm"
                                        style={{ width: "auto" }}
                                        value={selectedPreview}
                                        onChange={(e) => setSelectedPreview(Number(e.target.value))}
                                    >
                                        {previews.map((p, i) => (
                                            <option key={p.email + i} value={i}>{p.email}</option>
                                        ))}
                                    </Form.Select>
                                )}
                            </Card.Title>

                            {!preview ? (
                                <div className="text-muted py-5 text-center">
                                    Pick a template and recipients, then press Preview.
                                </div>
                            ) : (
                                <>
                                    <div className="mb-2">
                                        <strong>Subject:</strong> {preview.subject}
                                    </div>
                                    <div className="mb-2 d-flex gap-2">
                                        <Button size="sm" variant="outline-secondary" onClick={() => navigator.clipboard.writeText(preview.html)}>
                                            Copy HTML
                                        </Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => navigator.clipboard.writeText(preview.text)}>
                                            Copy plaintext
                                        </Button>
                                    </div>
                                    <iframe
                                        title="email-preview"
                                        srcDoc={preview.html}
                                        style={{ width: "100%", height: 600, border: "1px solid var(--bs-border-color)" }}
                                    />
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={confirming} onHide={() => setConfirming(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Send campaign?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    This will enqueue <strong>{recipients.length}</strong> email
                    {recipients.length === 1 ? "" : "s"} using the{" "}
                    <strong>{selectedTemplate?.name || templateId}</strong> template. This can't be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirming(false)} disabled={sending}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSend} disabled={sending}>
                        {sending ? <Spinner animation="border" size="sm" /> : `Send ${recipients.length}`}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default EmailCampaign;
